<!--
  The left sidebar for signed-in members (flag `sidebar-nav`).

  One component, three forms. The width comes from `--gm-sidebar-w` on the
  Layout root (see shell.css), and a container query turns a narrow sidebar
  into the icon rail, so the server renders the right form with no JavaScript:
    - 1200 px and wider: the full sidebar, 264 px, unless the member folds it;
    - 768 to 1199 px, or folded: the icon rail, 72 px, a tooltip on each icon;
    - under 768 px: hidden. The tab bar and the More sheet take over, and the
      sheet renders this component with `variant="sheet"`. The sheet leaves
      out the places of the tab bar and lists their sub-items as rows.
-->
<template>
  <nav
    class="gm-sb"
    :class="variant === 'sheet' ? 'gm-sb--sheet' : 'gm-sb--side'"
    aria-label="Main"
  >
    <div v-if="variant === 'side'" class="gm-sb-head">
      <div class="gm-sb-brand">
        <InertiaLink
          href="/dashboard"
          class="gm-sb-logo"
          aria-label="GrowthMentor home"
        >
          <img
            src="/growthmentor-logo.svg"
            alt=""
            class="gm-sb-wordmark"
            data-allow-mismatch
          />
          <img src="/gm-logomark.svg" alt="" class="gm-sb-mark" />
        </InertiaLink>
        <!-- Folded only: the button sits over the logomark and shows the
             panel icon on hover, so the mark itself opens the sidebar. -->
        <button
          v-tooltip="{
            content: 'Expand sidebar',
            placement: 'right',
            popperClass: 'gm-sb-tip-snap',
          }"
          type="button"
          class="gm-sb-unfold"
          aria-label="Expand the sidebar"
          @click="toggleCollapsed"
        >
          <PanelLeftIcon aria-hidden="true" />
        </button>
      </div>
      <div class="gm-sb-head-tools">
        <VDropdown
          :placement="bellPlacement"
          :aria-id="bellAriaId"
          class="flex"
          @apply-show="isBellOpen = true"
          @apply-hide="isBellOpen = false"
        >
          <button
            v-tooltip="{
              content: 'Notifications',
              placement: 'right',
              disabled: isBellOpen,
            }"
            type="button"
            class="gm-sb-bell"
            :class="{ 'is-active': isNotificationsPage || isBellOpen }"
            @click="markNotificationsAsSeen"
          >
            <BellIcon aria-hidden="true" />
            <span class="sr-only">Notifications</span>
            <!-- An open team invitation is the first row of the list, so
                 it lights the dot too. -->
            <span
              v-if="counts.notifications || hasBellInvite"
              class="gm-sb-bell-dot"
            >
              <span class="sr-only">
                , {{ counts.notifications + (hasBellInvite ? 1 : 0) }} new
              </span>
            </span>
          </button>
          <template #popper>
            <NotificationsDropdownPanel :is-focus-loop-active="isBellOpen" />
          </template>
        </VDropdown>
        <!-- Shows only on the open sidebar: the logomark expands the fold. -->
        <button
          v-tooltip="{
            content: 'Collapse sidebar',
            placement: 'right',
            popperClass: 'gm-sb-tip-snap',
          }"
          type="button"
          class="gm-sb-fold"
          aria-label="Collapse the sidebar"
          @click="toggleCollapsed"
        >
          <PanelLeftIcon aria-hidden="true" />
        </button>
      </div>
    </div>

    <div class="gm-sb-scroll">
      <SidebarProfileCard :is-rail="isRail" />

      <SidebarItem
        v-for="item in mainItems"
        :key="item.key"
        :item="item"
        :is-active="isActive"
        :is-rail="isRail"
      />

      <SidebarNextSession :is-rail="isRail" />
      <SidebarTimezoneRow
        :is-rail="isRail"
        :placement="variant === 'sheet' ? 'top-start' : 'right-start'"
      />

      <InertiaLink
        v-tooltip="{
          content: cta.label,
          placement: 'right',
          disabled: !isRail,
        }"
        :href="cta.href"
        class="gm-sb-cta"
      >
        <HeartHandshakeIcon
          v-if="cta.isMentor"
          class="gm-sb-icon"
          aria-hidden="true"
        />
        <PlusIcon v-else class="gm-sb-icon" aria-hidden="true" />
        <span class="gm-sb-label">{{ cta.label }}</span>
      </InertiaLink>

      <!-- The label needs two or more items under it. One item sits alone.
           With a label, the group folds behind its chevron. The icon rail
           has no label, so it always shows the icons. -->
      <div v-if="hasLabsGroup" class="gm-sb-group">
        <button
          type="button"
          class="gm-sb-group-toggle"
          :aria-expanded="isLabsShown"
          :aria-controls="labsId"
          @click="onLabsClick"
        >
          <!-- A flask that fizzes over. Drawn here: no icon set has the
               bubbles. They rise on hover. -->
          <svg
            class="gm-sb-group-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M9.5 10h5" />
            <path
              d="M10.5 10v3.2l-4.4 6.9A1.3 1.3 0 0 0 7.2 22h9.6a1.3 1.3 0 0 0 1.1-1.9l-4.4-6.9V10"
            />
            <path d="M7.6 18h8.8" />
            <circle class="gm-sb-bubble" cx="11" cy="6.3" r="1.4" />
            <circle class="gm-sb-bubble" cx="15" cy="3.4" r="1.8" />
            <circle class="gm-sb-bubble" cx="9.4" cy="2.2" r="1" />
          </svg>
          <span class="gm-sb-group-label">Labs</span>
          <ChevronDownIcon
            class="gm-sb-group-chevron"
            :class="{ 'is-open': isLabsShown }"
            aria-hidden="true"
          />
        </button>
      </div>

      <div
        :id="labsId"
        class="gm-sb-group-items"
        :class="{ 'is-closed': hasLabsGroup && !isLabsShown }"
      >
        <SidebarItem
          v-for="item in labs"
          :key="item.key"
          :item="item"
          :is-active="isActive"
          :is-rail="isRail"
        />
      </div>
    </div>

    <div class="gm-sb-foot">
      <SidebarNoticeCard :is-rail="isRail" />

      <InertiaLink
        v-if="whatsNew"
        v-tooltip="{
          content: `What's new`,
          placement: 'right',
          disabled: !isRail,
        }"
        href="/whats-new"
        class="gm-sb-item gm-sb-item--small"
        :class="{ 'is-active': isActive({ match: ['/whats-new'] }) }"
        @click="markWhatsNewSeen"
      >
        <MegaphoneIcon class="gm-sb-icon" aria-hidden="true" />
        <span class="gm-sb-label">What's new</span>
        <span v-if="hasUnseenWhatsNew" class="gm-sb-dot">
          <span class="sr-only">New updates</span>
        </span>
      </InertiaLink>

      <button
        v-tooltip="{ content: 'Help', placement: 'right', disabled: !isRail }"
        type="button"
        class="gm-sb-item gm-sb-item--small"
        @click="openHelp"
      >
        <LifeBuoyIcon class="gm-sb-icon" aria-hidden="true" />
        <span class="gm-sb-label">Help</span>
      </button>

      <div class="gm-sb-me">
        <NavbarUserDropdown
          :navigation="profileNavigation"
          :placement="menuPlacement"
          trigger-class="gm-sb-me-trigger"
        >
          <template #trigger="{ isLive }">
            <span class="gm-sb-avatar" :class="{ 'is-live': isLive }">
              <Avatar
                :src="currentUser.avatarUrl"
                :alt="`${currentUser.firstName} ${currentUser.lastName}`"
                disable-lazy-loading
              />
            </span>
            <span class="gm-sb-me-text">
              <b>{{ currentUser.firstName }} {{ currentUser.lastName }}</b>
              <span>{{ isLive ? 'Live now' : 'Your account' }}</span>
            </span>
            <ChevronUpIcon class="gm-sb-me-chevron" aria-hidden="true" />
          </template>
        </NavbarUserDropdown>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, toRef, useId, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import PanelLeftIcon from '~icons/lucide/panel-left'
import BellIcon from '~icons/lucide/bell'
import PlusIcon from '~icons/lucide/plus'
import HeartHandshakeIcon from '~icons/lucide/heart-handshake'
import MegaphoneIcon from '~icons/lucide/megaphone'
import LifeBuoyIcon from '~icons/lucide/life-buoy'
import ChevronUpIcon from '~icons/lucide/chevrons-up-down'
import ChevronDownIcon from '~icons/lucide/chevron-down'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import NavbarUserDropdown from '@/shared/components/navbar/NavbarUserDropdown.vue'
import NotificationsDropdownPanel from '@/modules/notifications/components/NotificationsDropdownPanel.vue'
import { useNavigationElements } from '@/shared/composables/navigation/useNavigationElements.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { useScreenWidth } from '@/shared/composables/useScreenWidth'
import { openSupportWidget } from '@/shared/composables/useHiddenSupportWidget'
import SidebarItem from './SidebarItem.vue'
import SidebarNoticeCard from './SidebarNoticeCard.vue'
import SidebarNextSession from './SidebarNextSession.vue'
import SidebarTimezoneRow from './SidebarTimezoneRow.vue'
import SidebarProfileCard from './SidebarProfileCard.vue'
import {
  PROMOTED_HREFS,
  RETIRED_HREFS,
  useSidebarNavigation,
} from './useSidebarNavigation'
import { useSidebarState } from './useSidebarState'
import { useShellNotices } from './useShellNotices'
import type { SidebarCounts } from './useSidebarCounts'

const props = withDefaults(
  defineProps<{
    counts: SidebarCounts
    variant?: 'side' | 'sheet'
    markMessagesAsRead: () => void
    markNotificationsAsSeen: () => void
  }>(),
  { variant: 'side' },
)

const page = usePage()
const { currentUser } = useCurrentUser()
const { collapsed, isMoreSheetOpen, isLabsOpen, toggleCollapsed, toggleLabs } =
  useSidebarState()
const { screenWidth, tailwindScreens } = useScreenWidth()
const { dropdownNavigation } = useNavigationElements()
const { teamInvite } = useShellNotices()
const hasBellInvite = computed(() => !!teamInvite.value)

const { main, sheetMain, labs, cta, isActive } = useSidebarNavigation(
  toRef(props, 'counts'),
  {
    markMessagesAsRead: () => props.markMessagesAsRead(),
  },
)

const mainItems = computed(() =>
  props.variant === 'sheet' ? sheetMain.value : main.value,
)

// In the icon rail only the orb shows, so the tooltip gives the name and
// what the button does.

const labsId = useId()
const hasLabsGroup = computed(() => labs.value.length > 1)

// On a Labs page the group is open, and the chevron can close it for
// this page only. Elsewhere it shows what the member chose last.
const isLabsPage = computed(() =>
  labs.value.some((item) => !item.comingSoon && isActive(item)),
)
const isLabsClosedHere = ref(false)
watch(
  () => page.url,
  () => {
    isLabsClosedHere.value = false
  },
)
const isLabsShown = computed(() =>
  isLabsPage.value ? !isLabsClosedHere.value : isLabsOpen.value,
)
const onLabsClick = () => {
  if (isLabsPage.value) {
    isLabsClosedHere.value = !isLabsClosedHere.value
    return
  }
  toggleLabs()
}

// The phone keeps the bell in its top bar, so the sheet shows no bell.
const isNotificationsPage = computed(() =>
  isActive({ match: ['/notifications'] }),
)

// Only the tooltips need to know the form; the CSS decides the form itself.
// screenWidth is 0 until mount, so the server renders no tooltips.
const isRail = computed(() => {
  if (props.variant === 'sheet' || !screenWidth.value) return false
  const md = tailwindScreens.value.md ?? 768
  const xl = tailwindScreens.value.xl ?? 1200
  return screenWidth.value >= md && (screenWidth.value < xl || collapsed.value)
})

// The menu opens upwards from the foot, and to the right from the icon rail.
const menuPlacement = computed(() =>
  props.variant === 'side' && isRail.value ? 'right-end' : 'top-start',
)

// The bell list drops down in the full sidebar and opens to the right from
// the icon rail, over the page.
const bellAriaId = useId()
const isBellOpen = ref(false)
const bellPlacement = computed(() =>
  isRail.value ? 'right-start' : 'bottom-start',
)

const openHelp = () => {
  isMoreSheetOpen.value = false
  openSupportWidget()
}

type MenuItem = { name?: string; href?: string; hasBottomSeparator?: boolean }

// The avatar menu of the top bar, less the items the sidebar lists itself and
// the retired ones. Switch profile, team, referrals, settings, help, feedback
// and log out stay. The removed items held the divider above Settings, so it
// moves up.
const hiddenHrefs = [...PROMOTED_HREFS, ...RETIRED_HREFS]
const profileNavigation = computed(() =>
  Object.fromEntries(
    Object.entries(dropdownNavigation.value).map(([role, items]) => {
      const kept = (items as MenuItem[]).filter(
        (item) => !hiddenHrefs.includes(item.href ?? ''),
      )
      return [
        role,
        kept.map((item, index) =>
          kept[index + 1]?.name === 'Settings'
            ? { ...item, hasBottomSeparator: true }
            : item,
        ),
      ]
    }),
  ),
)

type WhatsNewProp = { hasUnseen: boolean; watermark: string | null } | null

const whatsNew = computed(
  () => (page.props.shared as { whatsNew?: WhatsNewProp }).whatsNew ?? null,
)
const whatsNewSeenThisVisit = ref(false)
const hasUnseenWhatsNew = computed(
  () => !!whatsNew.value?.hasUnseen && !whatsNewSeenThisVisit.value,
)

// The same rule as the megaphone in the top bar: move the marker to the
// watermark the page was built with, and let the dot come back on failure.
const markWhatsNewSeen = () => {
  if (!whatsNew.value?.watermark || whatsNewSeenThisVisit.value) return
  whatsNewSeenThisVisit.value = true
  axios
    .post('/whats-new/seen', { watermark: whatsNew.value.watermark })
    .catch(() => {
      whatsNewSeenThisVisit.value = false
    })
}
</script>

<style>
/* Unscoped on purpose: the rail rules key off the sidebar's own width through
   a container query, and the fold state lives on the Layout root. Every class
   starts with gm-sb so nothing leaks. */
.gm-sb {
  container: gm-sb / inline-size;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--color-white, #fff);
  color: var(--color-neutral-900);
}
.gm-sb--side {
  position: fixed;
  inset-block: 0;
  left: 0;
  z-index: 40;
  width: var(--gm-sidebar-w);
  border-right: 1px solid var(--color-neutral-100);
  display: none;
}
@media (min-width: 48rem) {
  .gm-sb--side {
    display: flex;
  }
}
.gm-sb--sheet {
  width: 100%;
}

.gm-sb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 1.25rem 0.75rem 0.75rem 1.25rem;
}
.gm-sb-logo {
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
}
.gm-sb-logo:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-sb-wordmark {
  width: 9.5rem;
  height: auto;
}
.gm-sb-mark {
  display: none;
  width: 0.9375rem;
  height: auto;
}
.gm-sb-head-tools {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}
.gm-sb-bell,
.gm-sb-fold {
  position: relative;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  color: var(--color-neutral-400);
}
.gm-sb-bell {
  color: var(--color-neutral-600);
}
.gm-sb-bell:hover,
.gm-sb-fold:hover {
  background: var(--color-neutral-50);
  color: var(--color-neutral-900);
}
.gm-sb-bell.is-active {
  background: var(--color-neutral-100);
  color: var(--color-neutral-900);
}
.gm-sb-bell:focus-visible,
.gm-sb-fold:focus-visible {
  outline: 2px solid var(--color-primary-500);
}
.gm-sb-bell svg {
  width: 1.25rem;
  height: 1.25rem;
}
.gm-sb-fold svg {
  width: 1.125rem;
  height: 1.125rem;
}
.gm-sb-bell-dot {
  position: absolute;
  top: 0.3125rem;
  right: 0.375rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--color-error-500);
  box-shadow: 0 0 0 2px #fff;
}
@media (max-width: 74.99rem) {
  .gm-sb-fold {
    display: none;
  }
}

/* A click on fold or unfold hides the button under the tooltip. The usual
   fade would then play out at the top-left corner, over the logo. */
.v-popper__popper.gm-sb-tip-snap.v-popper__popper--hidden {
  transition: none;
}

.gm-sb-brand {
  position: relative;
  display: grid;
  place-items: center;
}
.gm-sb-unfold {
  display: none;
}
/* The rail at 1200 px and wider is always the folded sidebar (narrower
   screens get the rail with no way to open it). The logomark becomes the
   expand button, and the separate fold button goes. */
@media (min-width: 75rem) {
  .gm-shell.is-collapsed .gm-sb-fold {
    display: none;
  }
  .gm-shell.is-collapsed .gm-sb-brand {
    width: 2.75rem;
    height: 2.75rem;
  }
  .gm-shell.is-collapsed .gm-sb-unfold {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    border-radius: 0.625rem;
    background: var(--color-white, #fff);
    color: var(--color-neutral-900);
    opacity: 0;
    transition: opacity 120ms ease;
  }
  .gm-shell.is-collapsed .gm-sb-unfold:hover,
  .gm-shell.is-collapsed .gm-sb-unfold:focus-visible {
    opacity: 1;
    background: var(--color-neutral-50);
  }
  .gm-shell.is-collapsed .gm-sb-unfold:focus-visible {
    outline: 2px solid var(--color-primary-500);
  }
  .gm-shell.is-collapsed .gm-sb-unfold svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.gm-sb-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.25rem 0.75rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  /* The middle scrolls on a very short window, with no visible track. */
  scrollbar-width: none;
}
.gm-sb-scroll::-webkit-scrollbar {
  display: none;
}
/* With no track, a cut row looks like the end of the list. While there is
   more below, the last rows fade out. A middle that fits has no scroll
   timeline, so it gets no fade; a browser with no scroll timeline shows the
   plain cut. */
@property --gm-sb-fade {
  syntax: '<length>';
  inherits: false;
  initial-value: 0px;
}
@supports (animation-timeline: scroll()) {
  .gm-sb--side .gm-sb-scroll {
    mask-image: linear-gradient(
      to bottom,
      #000 calc(100% - var(--gm-sb-fade)),
      transparent
    );
    animation: gm-sb-more linear both;
    animation-timeline: scroll(self);
  }
}
@keyframes gm-sb-more {
  0%,
  85% {
    --gm-sb-fade: 2.5rem;
  }
  100% {
    --gm-sb-fade: 0px;
  }
}

/* The middle part scrolls, so its rows keep their height instead of
   shrinking to fit a short screen. */
.gm-sb-scroll > *,
.gm-sb-foot > * {
  flex-shrink: 0;
}

.gm-sb-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  height: 2.5rem;
  padding: 0 0.625rem;
  border-radius: 0.625rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-neutral-600);
  text-align: left;
  transition:
    background-color 120ms ease,
    color 120ms ease;
}
/* Hover changes the text only. Only the active row has a fill, so a row
   below the mouse does not look like a second active row. */
.gm-sb-item:hover {
  color: var(--color-neutral-900);
}
.gm-sb-item.is-active:hover {
  background: var(--color-neutral-100);
}
.gm-sb-item:focus-visible,
.gm-sb-sub:focus-visible,
.gm-sb-cta:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: -2px;
}
.gm-sb-item.is-active {
  background: var(--color-neutral-100);
  color: var(--color-neutral-900);
  font-weight: 600;
}
.gm-sb-item.is-active .gm-sb-icon {
  color: var(--color-primary-500);
}
/* The group of the current page: the sub-item has the fill, the group row
   does not. One filled row at a time. */
.gm-sb-item.is-active.has-active-child {
  background: transparent;
}
.gm-sb-item.is-active.has-active-child:hover {
  background: transparent;
}
.gm-sb-row {
  position: relative;
}
.gm-sb-item.has-chevron {
  padding-right: 2.5rem;
}
.gm-sb-chevron {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  color: var(--color-neutral-400);
}
.gm-sb-chevron:hover {
  background: rgb(0 0 0 / 0.05);
  color: var(--color-neutral-900);
}
.gm-sb-chevron:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: -2px;
}
.gm-sb-chevron svg {
  width: 1rem;
  height: 1rem;
  transition: transform 150ms ease;
}
.gm-sb-chevron svg.is-open {
  transform: rotate(180deg);
}
.gm-sb-item--small {
  height: 2.25rem;
  font-size: 0.875rem;
}
.gm-sb-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex: none;
}
.gm-sb-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gm-sb-item.is-soon .gm-sb-label {
  color: var(--color-neutral-500);
}
.gm-sb-item.is-soon .gm-sb-icon {
  color: var(--color-neutral-400);
}
.gm-sb-badge {
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.4375rem;
  border-radius: 999px;
  background: var(--color-error-500);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  display: grid;
  place-items: center;
  font-variant-numeric: tabular-nums;
}
/* The count on a sub-item says "new", not "waits for you": the brand colour,
   at the right end of the row. */
.gm-sb-badge.is-sub {
  margin-left: auto;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  background: var(--color-secondary-600);
  font-size: 0.6875rem;
}
.gm-sb-soon {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-neutral-500);
  background: var(--color-neutral-50);
  box-shadow: inset 0 0 0 1px var(--color-neutral-100);
  border-radius: 999px;
  padding: 0.125rem 0.5rem;
}
/* A feature in testing. Same form as Soon, in a darker grey: the row is a
   live link, not a closed door. */
.gm-sb-beta {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-neutral-700);
  background: var(--color-neutral-50);
  box-shadow: inset 0 0 0 1px var(--color-neutral-200);
  border-radius: 999px;
  padding: 0.125rem 0.5rem;
}
.gm-sb-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--color-neutral-900);
  margin-left: auto;
}

.gm-sb-subs {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  margin: 0.125rem 0 0.375rem 2rem;
  padding-left: 0.625rem;
  border-left: 1px solid var(--color-neutral-100);
}
.gm-sb-sub {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2rem;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--color-neutral-500);
  text-align: left;
}
.gm-sb-sub:hover {
  color: var(--color-neutral-900);
}
.gm-sb-sub.is-active {
  background: var(--color-neutral-100);
  color: var(--color-neutral-900);
  font-weight: 600;
}
.gm-sb-sub .gm-sb-soon {
  margin-left: auto;
}
.gm-sb-live-dot {
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 999px;
  background: var(--color-success-500);
  box-shadow: 0 0 0 3px var(--color-success-50);
}
.gm-sb-item .gm-sb-live-dot {
  margin-left: auto;
  margin-right: 0.25rem;
}
.gm-sb-live-count {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-success-700);
  font-variant-numeric: tabular-nums;
}

.gm-sb-next {
  margin-top: 0.75rem;
  padding: 0.625rem;
  border-radius: 0.875rem;
  background: var(--color-neutral-50);
  box-shadow: inset 0 0 0 1px var(--color-neutral-100);
}
.gm-sb-next.is-now {
  background: var(--color-success-50);
  box-shadow: inset 0 0 0 1px var(--color-success-200);
}
.gm-sb-next-main {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.625rem;
}
.gm-sb-next-main:focus-visible,
.gm-sb-next-join:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-sb-next-face {
  width: 2.25rem;
  height: 2.25rem;
  flex: none;
  border-radius: 999px;
  object-fit: cover;
  color: var(--color-neutral-400);
}
.gm-sb-next-body {
  min-width: 0;
  display: grid;
  line-height: 1.3;
}
.gm-sb-next-tag {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-neutral-500);
}
.gm-sb-next-when {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-neutral-900);
}
.gm-sb-next.is-now .gm-sb-next-when {
  color: var(--color-success-800);
}
.gm-sb-next-who {
  font-size: 0.8125rem;
  color: var(--color-neutral-500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gm-sb-next-main:hover .gm-sb-next-when {
  text-decoration: underline;
  text-underline-offset: 2px;
}
.gm-sb-next-join {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.25rem;
  margin-top: 0.625rem;
  border-radius: 999px;
  background: var(--color-success-600);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
}
.gm-sb-next-join:hover {
  background: var(--color-success-700);
}

.gm-sb-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.75rem;
  margin: 0.75rem 0 0.5rem;
  border-radius: 999px;
  background: var(--color-secondary-600);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
  box-shadow: 0 6px 16px -6px rgb(118 48 245 / 0.55);
}
.gm-sb-cta:hover {
  background: var(--color-secondary-700);
}
.gm-sb-cta .gm-sb-label {
  flex: none;
}

.gm-sb-group {
  padding-top: 0.5rem;
}
.gm-sb-group-toggle {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  height: 2rem;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-neutral-500);
  text-align: left;
}
.gm-sb-group-toggle:hover {
  color: var(--color-neutral-900);
}
.gm-sb-group-toggle:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: -2px;
}
.gm-sb-group-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex: none;
  margin-right: 0.25rem;
  overflow: visible;
}
.gm-sb-group-toggle:hover .gm-sb-bubble {
  animation: gm-sb-fizz 900ms ease-out infinite;
}
.gm-sb-group-toggle:hover .gm-sb-bubble:nth-of-type(2) {
  animation-delay: 150ms;
}
.gm-sb-group-toggle:hover .gm-sb-bubble:nth-of-type(3) {
  animation-delay: 300ms;
}
@keyframes gm-sb-fizz {
  0% {
    transform: translateY(3px);
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  100% {
    transform: translateY(-2px);
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .gm-sb-group-toggle:hover .gm-sb-bubble {
    animation: none;
  }
}
.gm-sb-group-chevron {
  width: 0.875rem;
  height: 0.875rem;
  transform: rotate(-90deg);
  transition: transform 150ms ease;
}
.gm-sb-group-chevron.is-open {
  transform: none;
}
/* The wrapper adds no box: its rows stay in the column's flex gap. */
.gm-sb-group-items {
  display: contents;
}
.gm-sb-group-items.is-closed {
  display: none;
}

.gm-sb-foot {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.625rem 0.75rem 0.875rem;
  border-top: 1px solid var(--color-neutral-100);
}

/* --gm-sb-card-bg: the fill of the card. The ring round the dot of a notice
   takes it too (SidebarNoticeCard). */
.gm-sb-upgrade {
  --gm-sb-card-bg: var(--color-neutral-50);
  position: relative;
  display: block;
  margin-bottom: 0.5rem;
  padding: 0.875rem;
  border-radius: 0.875rem;
  background: var(--gm-sb-card-bg);
  box-shadow: inset 0 0 0 1px var(--color-neutral-100);
  color: var(--color-neutral-600);
  font-size: 0.8125rem;
  line-height: 1.45;
  transition: background-color 120ms ease;
}
.gm-sb-upgrade:hover {
  --gm-sb-card-bg: var(--color-neutral-100);
}
.gm-sb-upgrade:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-sb-upgrade b {
  color: var(--color-neutral-900);
  font-weight: 600;
}
.gm-sb-upgrade-body {
  position: relative;
  display: grid;
  gap: 0.5rem;
  text-wrap: pretty;
}
.gm-sb-upgrade-faces {
  display: flex;
}
.gm-sb-upgrade-faces img {
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 999px;
  object-fit: cover;
  box-shadow: 0 0 0 2px #fff;
  margin-right: -0.4375rem;
}
/* The link at the end of a card: the foot cards, and the state block of the
   profile card. The arrow is in the line of the text and takes no width of
   its own (the margins cancel it), so it never goes to a line by itself: it
   hangs in the padding, after the last word. */
.gm-sb-upgrade-go {
  padding-right: 1.25rem;
  color: var(--color-secondary-700);
  font-weight: 600;
}
.gm-sb-upgrade-go svg {
  display: inline;
  width: 0.875rem;
  height: 0.875rem;
  margin: 0 -1.25rem 0 0.375rem;
  vertical-align: -0.125rem;
}
.gm-sb-upgrade-icon {
  display: none;
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--color-secondary-600);
}

.gm-sb-me {
  margin-top: 0.375rem;
}
.gm-sb-me > div {
  display: block;
}
.gm-sb-me-trigger {
  display: flex;
  align-items: center;
  gap: 0.6875rem;
  width: 100%;
  padding: 0.375rem 0.5rem;
  border-radius: 0.75rem;
  text-align: left;
}
.gm-sb-me-trigger:hover {
  background: var(--color-neutral-50);
}
.gm-sb-me-trigger:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: -2px;
}
.gm-sb-avatar {
  display: inline-flex;
  flex: none;
  border-radius: 999px;
}
.gm-sb-avatar.is-live {
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px var(--color-success-500);
}
.gm-sb-me-text {
  flex: 1;
  min-width: 0;
  display: grid;
  line-height: 1.25;
}
.gm-sb-me-text b {
  font-size: 0.875rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gm-sb-me-text span {
  font-size: 0.75rem;
  color: var(--color-neutral-500);
}
.gm-sb-me-chevron {
  width: 1rem;
  height: 1rem;
  flex: none;
  color: var(--color-neutral-400);
}

/* The icon rail: the sidebar is narrow, so labels, tags and groups go. */
@container gm-sb (max-width: 7.5rem) {
  .gm-sb-head {
    flex-direction: column;
    padding: 1.25rem 0 0.75rem;
  }
  .gm-sb-head-tools {
    flex-direction: column;
    gap: 0.25rem;
  }
  .gm-sb-bell {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.625rem;
  }
  .gm-sb-bell-dot {
    top: 0.5rem;
    right: 0.5625rem;
  }
  .gm-sb-wordmark {
    display: none;
  }
  .gm-sb-mark {
    display: block;
  }
  .gm-sb-scroll {
    align-items: center;
    padding-inline: 0.75rem;
    scrollbar-width: none;
  }
  .gm-sb-item,
  .gm-sb-cta {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    justify-content: center;
  }
  .gm-sb-cta {
    margin: 0.625rem 0 0.375rem;
  }
  /* Hidden from sight, still read by screen readers: the icons are
     aria-hidden, so the label is the item's name. */
  .gm-sb-label,
  .gm-sb-soon,
  .gm-sb-beta,
  .gm-sb-live-count,
  .gm-sb-next-body,
  .gm-sb-me-text,
  .gm-sb-upgrade-body {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .gm-sb-item.has-chevron {
    padding: 0;
  }
  .gm-sb-subs,
  .gm-sb-chevron,
  .gm-sb-group-label,
  .gm-sb-me-chevron {
    display: none;
  }
  .gm-sb-item.is-soon::after {
    content: '';
    position: absolute;
    right: 0.4375rem;
    bottom: 0.4375rem;
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 999px;
    background: var(--color-neutral-300);
  }
  /* The Beta tag has no room in the rail: a dot stands for it. */
  .gm-sb-item.is-beta::after {
    content: '';
    position: absolute;
    right: 0.4375rem;
    bottom: 0.4375rem;
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 999px;
    background: var(--color-neutral-700);
  }
  .gm-sb-badge {
    position: absolute;
    top: 0.375rem;
    right: 0.375rem;
    min-width: 0.5625rem;
    width: 0.5625rem;
    height: 0.5625rem;
    padding: 0;
    font-size: 0;
    box-shadow: 0 0 0 2px #fff;
  }
  .gm-sb-dot {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
  .gm-sb-item .gm-sb-live-dot {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    margin: 0;
    box-shadow: 0 0 0 2px #fff;
  }
  .gm-sb-group {
    width: 2rem;
    height: 1px;
    padding: 0;
    margin: 0.5rem auto;
    background: var(--color-neutral-100);
  }
  .gm-sb-group-toggle {
    display: none;
  }
  .gm-sb-group-items.is-closed {
    display: contents;
  }
  .gm-sb-foot {
    align-items: center;
    padding-inline: 0;
  }
  .gm-sb-next {
    padding: 0;
    background: none;
    box-shadow: none;
  }
  .gm-sb-next.is-now {
    background: none;
    box-shadow: none;
  }
  .gm-sb-next-main {
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
  }
  .gm-sb-next.is-now .gm-sb-next-face {
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px var(--color-success-500);
  }
  .gm-sb-next-join {
    display: none;
  }
  .gm-sb-upgrade {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    display: grid;
    place-items: center;
    border-radius: 0.75rem;
  }
  .gm-sb-upgrade-icon {
    display: block;
  }
  .gm-sb-me-trigger {
    padding: 0.25rem;
    justify-content: center;
  }
}
</style>
